import { useEffect, useMemo } from 'react';
import type { ChangeEvent } from 'react';
import {
  describeSlugError,
  suggestSlugFromDisplayName,
  validateTenantSlug,
} from '../../../lib/tenant-slug.js';
import type { IntakeShellRenderProps } from './IntakeShell.js';

/**
 * wd-2.3.2 — Step 1 "Identity & business profile" for the prospect-
 * facing intake portal.
 *
 * Fields mirror `intakeStep1Schema` in `packages/config/src/intake-steps.ts`
 * (the server validator that runs on POST /step). Layout is mobile-first
 * and every interactive control inherits the `--ferro-tap: 44px` tap target
 * from `index.css` via the `.ferro-field__input` / `.ferro-btn` classes.
 *
 * Slug-availability gate: the schema requires `slugAvailabilityChecked:
 * literal(true)`, so the component ships a "Confirm slug" action. Clicking
 * it runs the same `validateTenantSlug` rules the Sprint 1.5 wizard uses
 * and, on pass, flips the flag. Real uniqueness is not checked here — the
 * intake portal is token-gated (not platform-admin-gated) so the existing
 * `/api/platform/tenants/slug-availability` endpoint is out of reach. Final
 * uniqueness is enforced at the D3.3 commit-to-tenant step, where the
 * admin reviewer sees the intended slug before provisioning.
 *
 * Vertical, country, and timezone seed sensible defaults on mount so a
 * prospect who accepts suggestions never hits "required" issues for
 * those fields.
 */

export const INTAKE_TIMEZONES = [
  'America/Denver',
  'America/Los_Angeles',
  'America/Chicago',
  'America/New_York',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
] as const;

export type IntakeTimezone = (typeof INTAKE_TIMEZONES)[number];

export const INTAKE_ENTITY_TYPES = [
  'LLC',
  'S-Corp',
  'C-Corp',
  'Sole Proprietor',
  'Partnership',
  'Other',
] as const;

export type IntakeEntityType = (typeof INTAKE_ENTITY_TYPES)[number];

type AddressDraft = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

type Step1Draft = {
  legalName: string;
  displayName: string;
  slug: string;
  slugTouched: boolean;
  slugAvailabilityChecked: boolean;
  vertical: 'pet-services';
  timezone: string;
  primaryAddress: AddressDraft;
  primaryPhone: string;
  websiteUrl: string;
  ein: string;
  entityType: string;
  billingContactEmail: string;
  billingContactName: string;
};

const EMPTY_ADDRESS: AddressDraft = {
  line1: '',
  line2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringField(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function readAddress(value: unknown): AddressDraft {
  if (!isRecord(value)) return { ...EMPTY_ADDRESS };
  return {
    line1: stringField(value.line1),
    line2: stringField(value.line2),
    city: stringField(value.city),
    state: stringField(value.state),
    postalCode: stringField(value.postalCode),
    country: stringField(value.country) || 'US',
  };
}

function readStep1(draft: Record<string, unknown>): Step1Draft {
  return {
    legalName: stringField(draft.legalName),
    displayName: stringField(draft.displayName),
    slug: stringField(draft.slug),
    slugTouched: draft.slugTouched === true,
    slugAvailabilityChecked: draft.slugAvailabilityChecked === true,
    vertical: 'pet-services',
    timezone: stringField(draft.timezone),
    primaryAddress: readAddress(draft.primaryAddress),
    primaryPhone: stringField(draft.primaryPhone),
    websiteUrl: stringField(draft.websiteUrl),
    ein: stringField(draft.ein),
    entityType: stringField(draft.entityType),
    billingContactEmail: stringField(draft.billingContactEmail),
    billingContactName: stringField(draft.billingContactName),
  };
}

function groupIssuesByPath(
  issues: ReadonlyArray<{ path: string; message: string }>,
): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const issue of issues) {
    const key = issue.path === '' ? '_root' : issue.path;
    const list = out[key] ?? [];
    list.push(issue.message);
    out[key] = list;
  }
  return out;
}

function browserTimezoneOrEmpty(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return (INTAKE_TIMEZONES as readonly string[]).includes(tz) ? tz : '';
  } catch {
    return '';
  }
}

function fieldErrorId(field: string): string {
  return `intake-step1-${field}-error`.replace(/\./g, '-');
}

export function Step1BusinessBasics({
  stepDraft,
  onChange,
  issues,
}: IntakeShellRenderProps): JSX.Element {
  const value = useMemo(() => readStep1(stepDraft), [stepDraft]);
  const errorsByPath = useMemo(() => groupIssuesByPath(issues), [issues]);

  // Seed vertical / country / timezone defaults on first mount so the
  // prospect never faces a "required" validation issue for a field the
  // portal pre-fills. Running once — subsequent keystrokes route through
  // the explicit onChange handlers.
  useEffect(() => {
    const patch: Record<string, unknown> = {};
    if (stepDraft.vertical !== 'pet-services') {
      patch.vertical = 'pet-services';
    }
    if (
      !isRecord(stepDraft.primaryAddress)
      || stringField((stepDraft.primaryAddress as Record<string, unknown>).country) === ''
    ) {
      const existing = isRecord(stepDraft.primaryAddress)
        ? (stepDraft.primaryAddress as Record<string, unknown>)
        : {};
      patch.primaryAddress = { ...existing, country: 'US' };
    }
    if (stringField(stepDraft.timezone) === '') {
      const browserTz = browserTimezoneOrEmpty();
      if (browserTz !== '') patch.timezone = browserTz;
    }
    if (Object.keys(patch).length > 0) onChange(patch);
    // Intentionally run once on mount: the handlers own ongoing edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-suggest slug from displayName whenever displayName changes — but
  // stop once the prospect has manually edited the slug (`slugTouched`).
  useEffect(() => {
    if (value.slugTouched) return;
    const suggested = suggestSlugFromDisplayName(value.displayName);
    if (suggested !== value.slug) {
      onChange({ slug: suggested, slugAvailabilityChecked: false });
    }
    // Only react to displayName + slugTouched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.displayName, value.slugTouched]);

  const onText
    = (field: keyof Step1Draft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      onChange({ [field]: e.target.value });
    };

  const onSlugChange = (e: ChangeEvent<HTMLInputElement>): void => {
    onChange({
      slug: e.target.value,
      slugTouched: true,
      // Any edit invalidates a prior confirmation — the prospect must
      // confirm the *new* slug before it counts.
      slugAvailabilityChecked: false,
    });
  };

  const onAddressField
    = (field: keyof AddressDraft) =>
    (e: ChangeEvent<HTMLInputElement>): void => {
      const current = value.primaryAddress;
      onChange({ primaryAddress: { ...current, [field]: e.target.value } });
    };

  const onTimezone = (e: ChangeEvent<HTMLSelectElement>): void => {
    onChange({ timezone: e.target.value });
  };

  const onEntityType = (e: ChangeEvent<HTMLSelectElement>): void => {
    onChange({ entityType: e.target.value });
  };

  const slugValidation = validateTenantSlug(value.slug);
  const slugFormatError
    = value.slug !== '' && !slugValidation.ok
      ? describeSlugError(slugValidation.error)
      : null;

  const onConfirmSlug = (): void => {
    if (!slugValidation.ok) return;
    onChange({ slugAvailabilityChecked: true });
  };

  const serverErrorsFor = (path: string): string[] => errorsByPath[path] ?? [];
  const inlineError = (path: string): string | null => {
    const list = serverErrorsFor(path);
    return list.length > 0 ? list.join(' · ') : null;
  };

  const legalNameError = inlineError('legalName');
  const displayNameError = inlineError('displayName');
  const slugServerError = inlineError('slug');
  const slugCheckedError = inlineError('slugAvailabilityChecked');
  const timezoneError = inlineError('timezone');
  const phoneError = inlineError('primaryPhone');
  const websiteError = inlineError('websiteUrl');
  const einError = inlineError('ein');
  const entityTypeError = inlineError('entityType');
  const billingEmailError = inlineError('billingContactEmail');
  const billingNameError = inlineError('billingContactName');
  const addressErrors: Record<keyof AddressDraft, string | null> = {
    line1: inlineError('primaryAddress.line1'),
    line2: inlineError('primaryAddress.line2'),
    city: inlineError('primaryAddress.city'),
    state: inlineError('primaryAddress.state'),
    postalCode: inlineError('primaryAddress.postalCode'),
    country: inlineError('primaryAddress.country'),
  };

  return (
    <form
      className="ferro-form ferro-intake__form"
      aria-label="Step 1 — Business basics"
      noValidate
      onSubmit={(e) => e.preventDefault()}
    >
      <p className="ferro-muted">
        Tell us about your business. These details feed your legal footer,
        branded URLs, and billing contact.
      </p>

      <label className="ferro-field">
        <span className="ferro-field__label">Legal business name</span>
        <input
          type="text"
          name="legalName"
          autoComplete="organization"
          required
          value={value.legalName}
          onChange={onText('legalName')}
          className="ferro-field__input"
          placeholder="Happy Tail Happy Dog LLC"
          aria-invalid={legalNameError ? true : undefined}
          aria-describedby={legalNameError ? fieldErrorId('legalName') : undefined}
          data-testid="intake-step1-legal-name"
        />
        {legalNameError ? (
          <span
            id={fieldErrorId('legalName')}
            className="ferro-form__error"
            role="alert"
          >
            {legalNameError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Display name</span>
        <input
          type="text"
          name="displayName"
          autoComplete="off"
          required
          value={value.displayName}
          onChange={onText('displayName')}
          className="ferro-field__input"
          placeholder="Happy Tail Happy Dog"
          aria-invalid={displayNameError ? true : undefined}
          aria-describedby={displayNameError ? fieldErrorId('displayName') : undefined}
          data-testid="intake-step1-display-name"
        />
        {displayNameError ? (
          <span
            id={fieldErrorId('displayName')}
            className="ferro-form__error"
            role="alert"
          >
            {displayNameError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">
          Subdomain slug{' '}
          <span className="ferro-muted">(auto-suggested; editable)</span>
        </span>
        <input
          type="text"
          name="slug"
          required
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          value={value.slug}
          onChange={onSlugChange}
          className="ferro-field__input"
          placeholder="happy-tail"
          aria-invalid={
            slugFormatError || slugServerError || slugCheckedError
              ? true
              : undefined
          }
          aria-describedby={
            slugFormatError || slugServerError
              ? fieldErrorId('slug')
              : 'intake-step1-slug-preview'
          }
          data-testid="intake-step1-slug"
        />
        <span id="intake-step1-slug-preview" className="ferro-muted">
          Preview: <code>{value.slug || 'your-slug'}.animalloversapp.com</code>
        </span>
        {slugFormatError ? (
          <span
            id={fieldErrorId('slug')}
            className="ferro-form__error"
            role="alert"
          >
            {slugFormatError}
          </span>
        ) : null}
        {slugServerError ? (
          <span
            id={!slugFormatError ? fieldErrorId('slug') : undefined}
            className="ferro-form__error"
            role="alert"
          >
            {slugServerError}
          </span>
        ) : null}
        {value.slugAvailabilityChecked ? (
          <span className="ferro-muted" data-testid="intake-step1-slug-confirmed">
            Slug confirmed. Final availability is verified at review.
          </span>
        ) : (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onConfirmSlug}
            disabled={!slugValidation.ok}
            data-testid="intake-step1-confirm-slug"
          >
            Confirm slug
          </button>
        )}
        {slugCheckedError && !value.slugAvailabilityChecked ? (
          <span className="ferro-form__error" role="alert">
            Confirm your slug before submitting this step.
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Primary phone</span>
        <input
          type="tel"
          name="primaryPhone"
          autoComplete="tel"
          required
          value={value.primaryPhone}
          onChange={onText('primaryPhone')}
          className="ferro-field__input"
          placeholder="+1 555 555 1234"
          aria-invalid={phoneError ? true : undefined}
          aria-describedby={phoneError ? fieldErrorId('primaryPhone') : undefined}
          data-testid="intake-step1-phone"
        />
        {phoneError ? (
          <span
            id={fieldErrorId('primaryPhone')}
            className="ferro-form__error"
            role="alert"
          >
            {phoneError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Website (optional)</span>
        <input
          type="url"
          name="websiteUrl"
          autoComplete="url"
          value={value.websiteUrl}
          onChange={onText('websiteUrl')}
          className="ferro-field__input"
          placeholder="https://example.com"
          aria-invalid={websiteError ? true : undefined}
          aria-describedby={websiteError ? fieldErrorId('websiteUrl') : undefined}
          data-testid="intake-step1-website"
        />
        {websiteError ? (
          <span
            id={fieldErrorId('websiteUrl')}
            className="ferro-form__error"
            role="alert"
          >
            {websiteError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Timezone</span>
        <select
          name="timezone"
          required
          value={value.timezone}
          onChange={onTimezone}
          className="ferro-field__input"
          aria-invalid={timezoneError ? true : undefined}
          aria-describedby={timezoneError ? fieldErrorId('timezone') : undefined}
          data-testid="intake-step1-timezone"
        >
          <option value="">Select a timezone…</option>
          {INTAKE_TIMEZONES.map((tz) => (
            <option key={tz} value={tz}>
              {tz}
            </option>
          ))}
        </select>
        {timezoneError ? (
          <span
            id={fieldErrorId('timezone')}
            className="ferro-form__error"
            role="alert"
          >
            {timezoneError}
          </span>
        ) : null}
      </label>

      <fieldset className="ferro-field ferro-intake__address">
        <legend className="ferro-field__label">Primary address</legend>

        <label className="ferro-field">
          <span className="ferro-field__label">Street address</span>
          <input
            type="text"
            name="primaryAddress.line1"
            autoComplete="address-line1"
            required
            value={value.primaryAddress.line1}
            onChange={onAddressField('line1')}
            className="ferro-field__input"
            placeholder="123 Main St"
            aria-invalid={addressErrors.line1 ? true : undefined}
            data-testid="intake-step1-address-line1"
          />
          {addressErrors.line1 ? (
            <span className="ferro-form__error" role="alert">
              {addressErrors.line1}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">Apt, suite, unit (optional)</span>
          <input
            type="text"
            name="primaryAddress.line2"
            autoComplete="address-line2"
            value={value.primaryAddress.line2}
            onChange={onAddressField('line2')}
            className="ferro-field__input"
            placeholder="Ste 200"
            aria-invalid={addressErrors.line2 ? true : undefined}
            data-testid="intake-step1-address-line2"
          />
          {addressErrors.line2 ? (
            <span className="ferro-form__error" role="alert">
              {addressErrors.line2}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">City</span>
          <input
            type="text"
            name="primaryAddress.city"
            autoComplete="address-level2"
            required
            value={value.primaryAddress.city}
            onChange={onAddressField('city')}
            className="ferro-field__input"
            placeholder="Denver"
            aria-invalid={addressErrors.city ? true : undefined}
            data-testid="intake-step1-address-city"
          />
          {addressErrors.city ? (
            <span className="ferro-form__error" role="alert">
              {addressErrors.city}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">State (2 letters)</span>
          <input
            type="text"
            name="primaryAddress.state"
            autoComplete="address-level1"
            required
            maxLength={2}
            value={value.primaryAddress.state}
            onChange={(e) => {
              const next = e.target.value.toUpperCase().slice(0, 2);
              onChange({
                primaryAddress: { ...value.primaryAddress, state: next },
              });
            }}
            className="ferro-field__input"
            placeholder="CO"
            aria-invalid={addressErrors.state ? true : undefined}
            data-testid="intake-step1-address-state"
          />
          {addressErrors.state ? (
            <span className="ferro-form__error" role="alert">
              {addressErrors.state}
            </span>
          ) : null}
        </label>

        <label className="ferro-field">
          <span className="ferro-field__label">ZIP code</span>
          <input
            type="text"
            name="primaryAddress.postalCode"
            autoComplete="postal-code"
            inputMode="numeric"
            required
            value={value.primaryAddress.postalCode}
            onChange={onAddressField('postalCode')}
            className="ferro-field__input"
            placeholder="80202"
            aria-invalid={addressErrors.postalCode ? true : undefined}
            data-testid="intake-step1-address-postal"
          />
          {addressErrors.postalCode ? (
            <span className="ferro-form__error" role="alert">
              {addressErrors.postalCode}
            </span>
          ) : null}
        </label>

        <input
          type="hidden"
          name="primaryAddress.country"
          value={value.primaryAddress.country || 'US'}
          data-testid="intake-step1-address-country"
        />
        {addressErrors.country ? (
          <span className="ferro-form__error" role="alert">
            {addressErrors.country}
          </span>
        ) : null}
      </fieldset>

      <label className="ferro-field">
        <span className="ferro-field__label">Entity type</span>
        <select
          name="entityType"
          required
          value={value.entityType}
          onChange={onEntityType}
          className="ferro-field__input"
          aria-invalid={entityTypeError ? true : undefined}
          aria-describedby={
            entityTypeError ? fieldErrorId('entityType') : undefined
          }
          data-testid="intake-step1-entity-type"
        >
          <option value="">Select entity type…</option>
          {INTAKE_ENTITY_TYPES.map((kind) => (
            <option key={kind} value={kind}>
              {kind}
            </option>
          ))}
        </select>
        {entityTypeError ? (
          <span
            id={fieldErrorId('entityType')}
            className="ferro-form__error"
            role="alert"
          >
            {entityTypeError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">
          EIN <span className="ferro-muted">(optional for sole proprietors)</span>
        </span>
        <input
          type="text"
          name="ein"
          autoComplete="off"
          inputMode="numeric"
          value={value.ein}
          onChange={onText('ein')}
          className="ferro-field__input"
          placeholder="12-3456789"
          aria-invalid={einError ? true : undefined}
          aria-describedby={einError ? fieldErrorId('ein') : undefined}
          data-testid="intake-step1-ein"
        />
        {einError ? (
          <span
            id={fieldErrorId('ein')}
            className="ferro-form__error"
            role="alert"
          >
            {einError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Billing contact name</span>
        <input
          type="text"
          name="billingContactName"
          autoComplete="name"
          required
          value={value.billingContactName}
          onChange={onText('billingContactName')}
          className="ferro-field__input"
          placeholder="Jane Owner"
          aria-invalid={billingNameError ? true : undefined}
          aria-describedby={
            billingNameError ? fieldErrorId('billingContactName') : undefined
          }
          data-testid="intake-step1-billing-name"
        />
        {billingNameError ? (
          <span
            id={fieldErrorId('billingContactName')}
            className="ferro-form__error"
            role="alert"
          >
            {billingNameError}
          </span>
        ) : null}
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Billing contact email</span>
        <input
          type="email"
          name="billingContactEmail"
          autoComplete="email"
          required
          value={value.billingContactEmail}
          onChange={onText('billingContactEmail')}
          className="ferro-field__input"
          placeholder="billing@example.com"
          aria-invalid={billingEmailError ? true : undefined}
          aria-describedby={
            billingEmailError ? fieldErrorId('billingContactEmail') : undefined
          }
          data-testid="intake-step1-billing-email"
        />
        {billingEmailError ? (
          <span
            id={fieldErrorId('billingContactEmail')}
            className="ferro-form__error"
            role="alert"
          >
            {billingEmailError}
          </span>
        ) : null}
      </label>
    </form>
  );
}
